import React from 'react';
import { Box, Typography } from '@lucid/lego';
import styles from './StatusMessage.module.scss';

/**
 * Props for the StatusMessage component
 */
type StatusMessageProps = {
  /** The message text to display */
  message: string;
  /** The type of message (success or error) */
  type: 'success' | 'error';
  /** The theme for styling the message */
  theme: 'light' | 'dark';
};

/**
 * Status Message Component
 * Displays success or error messages with appropriate styling
 *
 * @component
 * @param {StatusMessageProps} props - Component props
 * @param {string} props.message - The message text to display
 * @param {'success' | 'error'} props.type - The type of message (success or error)
 * @param {'light' | 'dark'} props.theme - The theme for styling the message
 * @returns {JSX.Element} Styled status message
 * @example
 * <StatusMessage message="Saved successfully!" type="success" theme="light" />
 */
export const StatusMessage: React.FC<StatusMessageProps> = ({ message, type, theme }) => {
  const themeClass = theme === 'dark' ? 'Dark' : 'Light';
  const className = styles[`${type}MessageText${themeClass}`];

  return (
    <Box
      data-testid={`status-message-${type}`}
      mb={16}
      pt={12}
      pb={12}
      pl={16}
      pr={16}
      className={className}
    >
      <Typography variant="body" size="medium" color="white" theme={theme}>
        {message}
      </Typography>
    </Box>
  );
};

StatusMessage.displayName = 'StatusMessage';
